
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Chip,
  Avatar,
  Button,
  Tab,
  Tabs,
  Badge,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  IconButton,
  Divider,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  AppBar,
  Toolbar,
  Container,
  Stack,
  useTheme,
  alpha,
  Pagination
} from '@mui/material';
import {
  Refresh,
  LocationOn,
  Phone,
  Person,
  LocalHospital,
  DirectionsCar,
  Timeline,
  Speed,
  AccessTime,
  Warning,
  Info,
  CheckCircle,
  Error,
  Close,
  Search,
  FilterList,
  GetApp,
  Settings,
  Notifications,
  PersonPin,
  Navigation,
  Group
} from '@mui/icons-material';
import { io } from 'socket.io-client';
import './LiveEmergencyTracking.css';

// Import Premium Emergency Map Component
import SimpleEmergencyMap from './SimpleEmergencyMap';

const LiveEmergencyTracking = ({ socket }) => {
  const theme = useTheme();
  const [emergencies, setEmergencies] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedEmergency, setSelectedEmergency] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');
  const [notificationData, setNotificationData] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [emergencyDetails, setEmergencyDetails] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  const lastUpdateRef = useRef(null);

  // Socket.IO connection using passed socket prop
  useEffect(() => {
    if (!socket) {
      console.error('No socket provided to LiveEmergencyTracking');
      setError('No socket connection available');
      setIsLoading(false);
      return;
    }

    // Join live tracking room
    socket.emit('joinLiveTracking', {});

    // Listen for initial data
    const handleLiveEmergencyData = (data) => {
      console.log('Live emergency data received:', data);
      setEmergencies(data.emergencies || []);
      setSummary(data.summary || {});
      setIsLoading(false);
      lastUpdateRef.current = new Date();
    };

    // Listen for live updates
    const handleLiveEmergencyUpdate = (updateData) => {
      console.log('Live emergency update received:', updateData);
      // Store detailed notification data with enhanced information
      const enhancedNotificationData = {
        ...updateData,
        emergencyId: updateData.emergencyId || updateData.groupId || 'EM-' + Date.now(),
        status: updateData.status || updateData.newStatus || 'Updated',
        location: typeof updateData.location === 'string' ? updateData.location : 
                 updateData.location?.pickup || updateData.location?.destination || 
                 updateData.pickUpLocation?.address || 'Location updating...',
        patientName: updateData.patientName || updateData.passenger?.name || 'Patient details updating...',
        severity: updateData.severity || updateData.emergencySeverity || 'Unknown',
        timestamp: new Date().toISOString()
      };
      setNotificationData(enhancedNotificationData);
      
      // Determine severity based on update type
      const severity = updateData.type === 'emergency_created' ? 'warning' :
                      updateData.type === 'emergency_accepted' ? 'success' :
                      updateData.type === 'emergency_completed' ? 'success' :
                      updateData.type === 'emergency_cancelled' ? 'error' :
                      'info';
      
      const message = updateData.type === 'emergency_created' ? 'New emergency reported' :
                     updateData.type === 'emergency_accepted' ? 'Emergency accepted by crew' :
                     updateData.type === 'emergency_completed' ? 'Emergency completed' :
                     updateData.type === 'emergency_cancelled' ? 'Emergency cancelled' :
                     updateData.type === 'location_update' ? 'Location updated' :
                     updateData.type === 'status_change' ? 'Status changed' :
                     `Emergency update: ${updateData.type}`;
      
      showNotification(message, severity, enhancedNotificationData);
      // Refresh data after receiving update
      refreshData();
    };

    // Listen for emergency details
    const handleEmergencyDetails = (details) => {
      console.log('Emergency details received:', details);
      setEmergencyDetails(details);
    };

    // Handle connection errors
    const handleError = (error) => {
      console.error('Socket error:', error);
      setError(error.message || 'Connection error');
      showNotification('Connection error occurred', 'error');
    };

    // Handle disconnection
    const handleDisconnect = () => {
      console.log('Disconnected from live tracking');
      setError('Disconnected from live tracking');
    };

    // Handle reconnection
    const handleConnect = () => {
      console.log('Connected to live tracking');
      setError(null);
      socket.emit('joinLiveTracking', {});
    };

    // Add event listeners
    socket.on('liveEmergencyData', handleLiveEmergencyData);
    socket.on('liveEmergencyUpdate', handleLiveEmergencyUpdate);
    socket.on('emergencyDetails', handleEmergencyDetails);
    socket.on('error', handleError);
    socket.on('disconnect', handleDisconnect);
    socket.on('connect', handleConnect);

    // Cleanup on unmount
    return () => {
      socket.off('liveEmergencyData', handleLiveEmergencyData);
      socket.off('liveEmergencyUpdate', handleLiveEmergencyUpdate);
      socket.off('emergencyDetails', handleEmergencyDetails);
      socket.off('error', handleError);
      socket.off('disconnect', handleDisconnect);
      socket.off('connect', handleConnect);
      socket.emit('leaveLiveTracking');
    };
  }, [socket]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      refreshData();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const showNotification = (message, severity = 'info') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
    
    // Auto-dismiss after 8 seconds
    setTimeout(() => {
      setSnackbarOpen(false);
    }, 8000);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending': return 'warning';
      case 'Accepted': return 'info';
      case 'InProgress':
      case 'PARTIAL_START': return 'primary';
      case 'Ended': return 'success';
      default: return 'default';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 100: return 'success';
      case 101: return 'warning';
      case 102: return 'error';
      default: return 'default';
    }
  };

  const getSeverityText = (severity) => {
    switch (severity) {
      case 100: return 'Mild';
      case 101: return 'Moderate';
      case 102: return 'Severe';
      default: return 'Unknown';
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  const getTripStatus = (crewMember) => {
    if (crewMember.endedAt) return 'Completed';
    if (crewMember.arrivedAt) return 'At Destination';
    if (crewMember.startedAt) return 'En Route';
    return crewMember.status || 'Pending';
  };

  const getTripStatusColor = (crewMember) => {
    if (crewMember.endedAt) return 'success';
    if (crewMember.arrivedAt) return 'warning';
    if (crewMember.startedAt) return 'info';
    return 'default';
  };

  const renderTripTimeline = (crewMember) => {
    const timeline = [];
    
    if (crewMember.startedAt) {
      timeline.push({
        label: 'Started',
        time: crewMember.startedAt,
        status: 'completed',
        icon: '🚀'
      });
    }
    
    if (crewMember.arrivedAt) {
      timeline.push({
        label: 'Arrived',
        time: crewMember.arrivedAt,
        status: 'completed',
        icon: '🏥'
      });
    }
    
    if (crewMember.endedAt) {
      timeline.push({
        label: 'Completed',
        time: crewMember.endedAt,
        status: 'completed',
        icon: '✅'
      });
    }
    
    return timeline;
  };

  const getTimeSinceCreated = (createdAt) => {
    const now = new Date();
    const created = new Date(createdAt);
    const diffMinutes = Math.floor((now - created) / (1000 * 60));
    
    if (diffMinutes < 60) {
      return `${diffMinutes}m ago`;
    } else if (diffMinutes < 1440) {
      return `${Math.floor(diffMinutes / 60)}h ago`;
    } else {
      return `${Math.floor(diffMinutes / 1440)}d ago`;
    }
  };

  const handleViewDetails = (emergency) => {
    setSelectedEmergency(emergency);
    if (socket) {
      socket.emit('getEmergencyDetails', { emergencyGroupId: emergency.groupId });
    }
    setDetailsDialogOpen(true);
  };

  const handleMapMarkerClick = (emergency) => {
    setSelectedMarker(emergency);
    setSelectedEmergency(emergency);
  };

  const refreshData = () => {
    if (socket) {
      socket.emit('joinLiveTracking', {});
    }
  };

  const filteredEmergencies = emergencies.filter(emergency => {
    const matchesStatus = filterStatus === 'all' || emergency.status === filterStatus;
    const matchesSearch = !searchQuery || 
      emergency.groupId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emergency.passenger?.name?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  // Pagination setup
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const totalPages = Math.ceil(filteredEmergencies.length / itemsPerPage);

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const paginatedEmergencies = filteredEmergencies.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Generate readable emergency IDs
  const emergencyIdMap = {};
  paginatedEmergencies.forEach((em, idx) => {
    emergencyIdMap[em.groupId] = `EM-${idx + 1}`;
  });

  // Enhanced Summary Statistics Component
  const SummaryStats = () => (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      <Grid item xs={12} sm={6} md={3}>
        <Card 
          sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: 'rgba(255,255,255,0.1)',
              borderRadius: '50%',
              transform: 'translate(30px, -30px)'
            }
          }}
        >
          <CardContent sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {summary?.total || 0}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Total Active Emergencies
                </Typography>
              </Box>
              <LocalHospital sx={{ fontSize: 40, opacity: 0.8 }} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
              <Box sx={{ width: 8, height: 8, backgroundColor: '#4caf50', borderRadius: '50%', mr: 1 }} />
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Live tracking active
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card 
          sx={{ 
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: 'rgba(255,255,255,0.1)',
              borderRadius: '50%',
              transform: 'translate(30px, -30px)'
            }
          }}
        >
          <CardContent sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {summary?.pending || 0}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Pending Response
                </Typography>
              </Box>
              <AccessTime sx={{ fontSize: 40, opacity: 0.8 }} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
              <Box sx={{ width: 8, height: 8, backgroundColor: '#ff9800', borderRadius: '50%', mr: 1 }} />
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Awaiting crew assignment
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card 
          sx={{ 
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: 'rgba(255,255,255,0.1)',
              borderRadius: '50%',
              transform: 'translate(30px, -30px)'
            }
          }}
        >
          <CardContent sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {summary?.accepted || 0}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Accepted
                </Typography>
              </Box>
              <CheckCircle sx={{ fontSize: 40, opacity: 0.8 }} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
              <Box sx={{ width: 8, height: 8, backgroundColor: '#2196f3', borderRadius: '50%', mr: 1 }} />
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Crew assigned & ready
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card 
          sx={{ 
            background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
            color: '#333',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: 'rgba(255,255,255,0.3)',
              borderRadius: '50%',
              transform: 'translate(30px, -30px)'
            }
          }}
        >
          <CardContent sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {summary?.inProgress || 0}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  In Progress
                </Typography>
              </Box>
              <DirectionsCar sx={{ fontSize: 40, opacity: 0.7 }} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
              <Box sx={{ width: 8, height: 8, backgroundColor: '#9c27b0', borderRadius: '50%', mr: 1 }} />
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                En route to destination
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  // Enhanced Emergency Table Component
  const EmergencyTable = () => (
    <TableContainer 
      component={Paper} 
      sx={{ 
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        borderRadius: 2,
        overflow: 'hidden'
      }}
    >
      <Table>
        <TableHead sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
              Emergency Info
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
              Patient Details
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
              Status
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
              Severity
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
              Crew Status
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
              Location
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
              Time Info
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedEmergencies.map((emergency, index) => (
            <TableRow
              key={emergency.groupId} 
              sx={{
                '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.05) },
                backgroundColor: index % 2 === 0 ? 'transparent' : 
                alpha(theme.palette.grey[100], 0.5)
              }}
            >
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar 
                    sx={{ 
                      mr: 2, 
                      backgroundColor: emergency.severity === 102 ? '#f44336' : emergency.severity === 101 ? '#ff9800' : '#4caf50',
                      width: 48,
                      height: 48
                    }}
                  >
                    <LocalHospital />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    EM-{(currentPage - 1) * itemsPerPage + index + 1}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Distance: {emergency.distance}km
                    </Typography>
                  </Box>
                </Box>
              </TableCell>

              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ mr: 2, backgroundColor: theme.palette.info.light }}>
                    <Person />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                      {emergency.passenger?.name || 'Unknown'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      <Phone sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        {emergency.passenger?.phone || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </TableCell>

              <TableCell>
                <Chip
                  label={emergency.status}
                  color={getStatusColor(emergency.status)}
                  variant="outlined"
                  sx={{ fontWeight: 'medium' }}
                />
              </TableCell>

              <TableCell>
                <Chip
                  label={getSeverityText(emergency.severity)}
                  color={getSeverityColor(emergency.severity)}
                  sx={{ fontWeight: 'medium' }}
                />
              </TableCell>

              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Badge 
                    badgeContent={emergency.totalCrewMembers} 
                    color="primary"
                    sx={{ mr: 2 }}
                  >
                    <Group color="action" />
                  </Badge>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {emergency.activeCrewMembers} active
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      of {emergency.totalCrewMembers} total
                    </Typography>
                  </Box>
                </Box>
              </TableCell>

              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', maxWidth: 200 }}>
                  <LocationOn sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                  <Tooltip title={emergency.pickUpLocation?.address || 'Unknown'}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {emergency.pickUpLocation?.address || 'Unknown'}
                    </Typography>
                  </Tooltip>
                </Box>
              </TableCell>

              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AccessTime sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {getTimeSinceCreated(emergency.createdAt)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatTime(emergency.createdAt)}
                    </Typography>
                  </Box>
                </Box>
              </TableCell>

              <TableCell>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Info />}
                  onClick={() => handleViewDetails(emergency)}
                  sx={{ 
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 'medium'
                  }}
                >
                  Details
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // Pagination Controls Component
  const PaginationControls = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
      <Pagination 
        count={totalPages} 
        page={currentPage} 
        onChange={handlePageChange} 
        color="primary"
        shape="rounded"
        showFirstButton 
        showLastButton
      />
    </Box>
  );

  // Premium Emergency Map Component
  const EmergencyMap = () => {
    return (
          <SimpleEmergencyMap
            emergencies={paginatedEmergencies}
            selectedMarker={selectedMarker}
            handleMapMarkerClick={handleMapMarkerClick}
            setSelectedMarker={setSelectedMarker}
            getSeverityText={getSeverityText}
            emergencyIdMap={emergencyIdMap}
          />
    );
  };

  // Enhanced Emergency Details Dialog
  const EmergencyDetailsDialog = () => (
    <Dialog
      open={detailsDialogOpen}
      onClose={() => setDetailsDialogOpen(false)}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          position: 'relative',
          py: 3
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LocalHospital sx={{ mr: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Emergency Details
            </Typography>
          </Box>
          <IconButton
            onClick={() => setDetailsDialogOpen(false)}
            sx={{ 
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {selectedEmergency && (
          <Box sx={{ p: 3 }}>
            {/* Patient Information Card */}
            <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ mr: 2, backgroundColor: theme.palette.info.main }}>
                    <PersonPin />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Patient Information
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Full Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                      {selectedEmergency.passenger?.name || 'Unknown'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Contact Number
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                      {selectedEmergency.passenger?.phone || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Emergency ID
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                      {selectedEmergency.groupId}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Severity Level
                    </Typography>
                    <Chip
                      label={getSeverityText(selectedEmergency.severity)}
                      color={getSeverityColor(selectedEmergency.severity)}
                      sx={{ fontWeight: 'medium' }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Location Information Card */}
            <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ mr: 2, backgroundColor: theme.palette.success.main }}>
                    <LocationOn />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Location Information
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Pickup Location
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                      {selectedEmergency.pickUpLocation?.address || 'Unknown'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Destination
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                      {selectedEmergency.destinationLocation?.address || 'Unknown'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Total Distance
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                      {selectedEmergency.distance} km
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            {/* Crew Members Card */}
            <Card sx={{ borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ mr: 2, backgroundColor: theme.palette.warning.main }}>
                    <DirectionsCar />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Crew Members
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={2}>
                  {selectedEmergency.crewMembers.map((crew) => (
                    <Paper 
                      key={crew.driverId} 
                      sx={{ 
                        p: 2, 
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.primary.main, 0.02),
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ mr: 2, backgroundColor: theme.palette.primary.main }}>
                            <Person />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                              {crew.driverName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {crew.vehicleType} Driver
                            </Typography>
                          </Box>
                        </Box>
                        <Chip
                          label={crew.status}
                          color={getStatusColor(crew.status)}
                          variant="outlined"
                          sx={{ fontWeight: 'medium' }}
                        />
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Contact Number
                          </Typography>
                          <Typography variant="body1">
                            {crew.driverPhone}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Vehicle Details
                          </Typography>
                          <Typography variant="body1">
                            {crew.vehicleColor} - {crew.vehiclePlate}
                          </Typography>
                        </Grid>
                        {crew.currentLocation && (
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Navigation sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary">
                                Last location update: {formatTime(crew.currentLocation.lastUpdated)}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        
                        {/* Trip Timeline */}
                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 'bold' }}>
                            Trip Timeline
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {/* Started */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box 
                                sx={{ 
                                  width: 8, 
                                  height: 8, 
                                  borderRadius: '50%', 
                                  backgroundColor: crew.startedAt ? '#4caf50' : '#e0e0e0' 
                                }} 
                              />
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: crew.startedAt ? 'text.primary' : 'text.secondary',
                                  fontWeight: crew.startedAt ? 'medium' : 'normal'
                                }}
                              >
                                Started: {crew.startedAt ? formatDateTime(crew.startedAt).time : 'Not started'}
                              </Typography>
                            </Box>
                            
                            {/* Arrived */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box 
                                sx={{ 
                                  width: 8, 
                                  height: 8, 
                                  borderRadius: '50%', 
                                  backgroundColor: crew.arrivedAt ? '#ff9800' : '#e0e0e0' 
                                }} 
                              />
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: crew.arrivedAt ? 'text.primary' : 'text.secondary',
                                  fontWeight: crew.arrivedAt ? 'medium' : 'normal'
                                }}
                              >
                                Arrived: {crew.arrivedAt ? formatDateTime(crew.arrivedAt).time : 'Not arrived'}
                              </Typography>
                            </Box>
                            
                            {/* Completed */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box 
                                sx={{ 
                                  width: 8, 
                                  height: 8, 
                                  borderRadius: '50%', 
                                  backgroundColor: crew.endedAt ? '#2196f3' : '#e0e0e0' 
                                }} 
                              />
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: crew.endedAt ? 'text.primary' : 'text.secondary',
                                  fontWeight: crew.endedAt ? 'medium' : 'normal'
                                }}
                              >
                                Completed: {crew.endedAt ? formatDateTime(crew.endedAt).time : 'Not completed'}
                              </Typography>
                            </Box>
                            
                            {/* Trip Status Chip */}
                            <Box sx={{ mt: 1 }}>
                              <Chip
                                label={getTripStatus(crew)}
                                color={getTripStatusColor(crew)}
                                size="small"
                                variant="outlined"
                                sx={{ fontWeight: 'medium' }}
                              />
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, backgroundColor: alpha(theme.palette.grey[100], 0.5) }}>
        <Button 
          onClick={() => setDetailsDialogOpen(false)}
          variant="contained"
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 'medium',
            px: 4
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Enhanced Header */}
      <AppBar 
        position="static" 
        sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}
      >
        <Toolbar sx={{ py: 1 }}>
          <Container maxWidth="xl">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              {/* <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LocalHospital sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    Live Emergency Tracking
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Real-time monitoring and coordination system
                  </Typography>
                </Box>
              </Box> */}
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {lastUpdateRef.current && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: 8, height: 8, backgroundColor: '#4caf50', borderRadius: '50%', mr: 1 }} />
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Last updated: {formatTime(lastUpdateRef.current)}
                    </Typography>
                  </Box>
                )}
                
                <Button
                  variant="contained"
                  startIcon={<Refresh />}
                  onClick={refreshData}
                  disabled={isLoading}
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' },
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 'medium'
                  }}
                >
                  {isLoading ? 'Refreshing...' : 'Refresh'}
                </Button>
                
                <IconButton 
                  sx={{ 
                    color: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
                  }}
                >
                  <Notifications />
                </IconButton>
                
                <IconButton 
                  sx={{ 
                    color: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
                  }}
                >
                  <Settings />
                </IconButton>
              </Box>
            </Box>
          </Container>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Error Alert */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3, 
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}
          >
            {error}
          </Alert>
        )}

        {/* Summary Statistics */}
        <SummaryStats />

        {/* Search and Filter Controls */}
        <Paper 
          sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search emergencies..."
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              sx={{ 
                minWidth: 250,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            />
            
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Filter by Status</InputLabel>
              <Select
                value={filterStatus}
                label="Filter by Status"
                onChange={(e) => setFilterStatus(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Accepted">Accepted</MenuItem>
                <MenuItem value="InProgress">In Progress</MenuItem>
                <MenuItem value="Ended">Ended</MenuItem>
              </Select>
            </FormControl>
            
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 'medium'
              }}
            >
              Advanced Filters
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<GetApp />}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 'medium'
              }}
            >
              Export Data
            </Button>
          </Box>
        </Paper>

        {/* Main Content */}
        <Paper 
          sx={{ 
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}
        >
          <Tabs 
            value={selectedTab} 
            onChange={(event, newValue) => setSelectedTab(newValue)}
            sx={{ 
              borderBottom: 1, 
              borderColor: 'divider',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 'medium',
                fontSize: '0.95rem'
              }
            }}
          >
            <Tab 
              label="Table View" 
              icon={<Timeline />} 
              iconPosition="start"
              sx={{ minHeight: 64 }}
            />
            <Tab 
              label="Map View" 
              icon={<LocationOn />} 
              iconPosition="start"
              sx={{ minHeight: 64 }}
            />
          </Tabs>
          
          <Box sx={{ p: 3 }}>
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                <CircularProgress size={48} />
                <Typography variant="h6" sx={{ ml: 3 }}>
                  Loading emergency data...
                </Typography>
              </Box>
            ) : (
              <>
                {selectedTab === 0 && (
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                      Active Emergencies ({filteredEmergencies.length})
                    </Typography>
                    <EmergencyTable />
                    {totalPages > 1 && <PaginationControls />}
                  </Box>
                )}
                
                {selectedTab === 1 && (
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                      Emergency Locations Map
                    </Typography>
                    <EmergencyMap />
                  </Box>
                )}
              </>
            )}
          </Box>
        </Paper>
      </Container>

      {/* Emergency Details Dialog */}
      <EmergencyDetailsDialog />

      {/* Enhanced Notification Card */}
      {snackbarOpen && (
        <Paper
          sx={{
            position: 'fixed',
            top: 20,
            right: 20,
            width: 400,
            zIndex: 9999,
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
            overflow: 'hidden',
            animation: 'slideInRight 0.5s ease-out',
            '@keyframes slideInRight': {
              '0%': {
                transform: 'translateX(100%)',
                opacity: 0
              },
              '100%': {
                transform: 'translateX(0)',
                opacity: 1
              }
            }
          }}
        >
          {/* Header */}
          <Box
            sx={{
              background: snackbarSeverity === 'error' 
                ? 'linear-gradient(135deg, #ff4757 0%, #ff3742 100%)'
                : snackbarSeverity === 'warning'
                ? 'linear-gradient(135deg, #ffa726 0%, #ff9800 100%)'
                : snackbarSeverity === 'success'
                ? 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)'
                : 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
              color: 'white',
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar
                sx={{
                  mr: 2,
                  bgcolor: 'rgba(255,255,255,0.2)',
                  width: 48,
                  height: 48
                }}
              >
                {snackbarSeverity === 'error' ? (
                  <Error sx={{ fontSize: 24 }} />
                ) : snackbarSeverity === 'warning' ? (
                  <Warning sx={{ fontSize: 24 }} />
                ) : snackbarSeverity === 'success' ? (
                  <CheckCircle sx={{ fontSize: 24 }} />
                ) : (
                  <LocalHospital sx={{ fontSize: 24 }} />
                )}
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  {snackbarSeverity === 'error' ? 'Error Alert' :
                   snackbarSeverity === 'warning' ? 'Warning' :
                   snackbarSeverity === 'success' ? 'Success' :
                   'Emergency Update'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {new Date().toLocaleTimeString()}
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={() => setSnackbarOpen(false)}
              sx={{
                color: 'white',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
              }}
            >
              <Close />
            </IconButton>
          </Box>

          {/* Content */}
          <CardContent sx={{ p: 3 }}>
            <Typography variant="body1" sx={{ mb: 2, fontWeight: 'medium' }}>
              {snackbarMessage}
            </Typography>
            
            {/* Show additional details if available */}
            {notificationData && (
              <Box sx={{ mt: 2 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Emergency Details:
                </Typography>
                <Grid container spacing={1}>
                  {notificationData.emergencyId && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        ID: {notificationData.emergencyId}
                      </Typography>
                    </Grid>
                  )}
                  {notificationData.status && (
                    <Grid item xs={12} sm={6}>
                      <Chip
                        size="small"
                        label={notificationData.status}
                        color={getStatusColor(notificationData.status)}
                        sx={{ mt: 0.5 }}
                      />
                    </Grid>
                  )}
                  {notificationData.location && typeof notificationData.location === 'string' && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <LocationOn sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {notificationData.location}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {notificationData.patientName && typeof notificationData.patientName === 'string' && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <Person sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          Patient: {notificationData.patientName}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}
          </CardContent>

          {/* Action buttons */}
          <Box sx={{ p: 2, pt: 0, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setSnackbarOpen(false)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 'medium'
              }}
            >
              Dismiss
            </Button>
            {notificationData && (
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  // Find the emergency from the notification data
                  const emergency = emergencies.find(em => 
                    em.groupId === notificationData.emergencyId || 
                    em.groupId === notificationData.groupId
                  );
                  
                  if (emergency) {
                    // Set the selected emergency and open details dialog
                    setSelectedEmergency(emergency);
                    if (socket) {
                      socket.emit('getEmergencyDetails', { emergencyGroupId: emergency.groupId });
                    }
                    setDetailsDialogOpen(true);
                  }
                  
                  // Close the notification
                  setSnackbarOpen(false);
                }}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 'medium'
                }}
              >
                View Details
              </Button>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default LiveEmergencyTracking;
